import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { map, orderBy, range } from 'lodash';
import MoedaText from '../ui/inputs/moedaText';
import copy from 'copy-to-clipboard';
import { Moment } from 'moment';
import InteiroText from '../ui/inputs/inteiroText';
import PercentualText from '../ui/inputs/percentualText';
import { useNavigate } from 'react-router-dom';

export type TabelaBaseHeader = {
  titulo: string;
  key: string;
  ordem: number;
  align: 'left' | 'center' | 'right';
};

type Props = {
  header: TabelaBaseHeader[];
  rows: { [key: string]: JSX.Element }[];
  footer?: { [key: string]: JSX.Element };
  maxWidth?: number;
  loading?: boolean;
};

export default function TabelaBase({
  header,
  rows,
  footer,
  maxWidth,
  loading = false,
}: Props) {
  return (
    <TableContainer
      sx={{
        overflow: 'auto',
        height: maxWidth ? `calc(${maxWidth}px - 24px - 16px)` : 'auto',
        '&::-webkit-scrollbar': {
          width: 12,
        },
        '&::-webkit-scrollbar-track': {
          background: (theme) => theme.palette.ink['08'],
          backgroundClip: 'padding-box !important',
          borderLeft: '4px transparent solid',
        },
        '&::-webkit-scrollbar-thumb': {
          background: (theme) => theme.palette.ink[20],
          backgroundClip: 'padding-box !important',
          borderLeft: '4px transparent solid',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: (theme) => theme.palette.ink[40],
        },
      }}
    >
      <Table
        stickyHeader
        size="small"
        sx={{
          // Header: mono uppercase ink-60 vem do tema; aqui só densidade e a
          // hairline que separa cabeçalho do corpo (DESIGN.md → Tabelas).
          '& .MuiTableCell-head': {
            px: 1,
            py: 1,
            lineHeight: 1.3,
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
            '&:first-of-type': { pl: 2 },
            '&:last-of-type': { pr: 2 },
          },
          '& .MuiTableCell-body': {
            px: 1,
            py: 0.75,
            '&:first-of-type': { pl: 2 },
            '&:last-of-type': { pr: 2 },
            // Sem `color` aqui de propósito: a célula já herda
            // `text.primary` do TableCell, e assim o `sx` de cada
            // célula não é sobrescrito por especificidade.
            '& .MuiTypography-root': {
              '&.error': {
                color: 'error.main',
              },
              '&.success': {
                color: 'success.main',
              },
            },
          },
          // Linha de total: hairline em cima + fill de tinta sutil, sem sombra.
          '& .MuiTableCell-footer': {
            px: 1,
            py: 1,
            borderBottom: 'none',
            borderTop: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: (theme) => theme.palette.ink['08'],
            color: 'text.primary',
            '&:first-of-type': { pl: 2 },
            '&:last-of-type': { pr: 2 },
            '& .MuiTypography-root': {
              fontWeight: 600,
              '&.error': {
                color: 'error.main',
              },
              '&.success': {
                color: 'success.main',
              },
            },
          },
          // Hover de linha é só para o corpo.
          '& .MuiTableHead-root .MuiTableRow-root:hover': {
            backgroundColor: 'transparent',
          },
          '& .MuiTableFooter-root .MuiTableRow-root:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <TableHead>
          <TableRow>
            {map(orderBy(header, 'ordem'), (cell, index) => (
              <TableCell key={index} align={cell.align}>
                {cell.titulo}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading
            ? map(range(4), (index) => (
                <TableRow hover key={index}>
                  {map(orderBy(header, 'ordem'), (column, index) => (
                    <TableCell key={index} align={column.align}>
                      <Skeleton variant="text" animation={false} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            : map(rows, (cell, index) => (
                <TableRow hover key={index}>
                  {map(orderBy(header, 'ordem'), (column, index) => (
                    <TableCell key={index} align={column.align}>
                      {cell[column.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
        </TableBody>
        {footer && (
          <TableFooter>
            <TableRow>
              {map(orderBy(header, 'ordem'), (column, index) => (
                <TableCell key={index} align={column.align}>
                  {footer[column.key]}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}

export function CelulaData({
  data,
  format = 'DD/MM/YYYY',
  copyOnClick = false,
}: {
  data: Moment;
  format?: string;
  copyOnClick?: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  return (
    <Typography
      variant="dataMono"
      onClick={
        copyOnClick
          ? (e) => copy((e.target as HTMLElement).innerText)
          : () => navigate(`/checkpoint#${data.format('YYYYMMDD')}`)
      }
      sx={{ cursor: 'pointer' }}
    >
      {data.format(format)}
    </Typography>
  );
}

export function CelulaTexto({
  texto,
  copyOnClick = false,
}: {
  texto?: string;
  copyOnClick?: boolean;
}): JSX.Element {
  return (
    <Typography
      onClick={
        copyOnClick
          ? (e) => copy((e.target as HTMLElement).innerText)
          : undefined
      }
      sx={[copyOnClick && { cursor: 'pointer' }]}
    >
      {texto}
    </Typography>
  );
}

export function CelulaValor({
  valor,
  copyOnClick = false,
  secret = false,
}: {
  valor: number;
  copyOnClick?: boolean;
  secret?: boolean;
}): JSX.Element {
  return (
    <Typography
      variant="dataMono"
      onClick={
        copyOnClick
          ? (e) => copy((e.target as HTMLElement).innerText.replace('R$ ', ''))
          : undefined
      }
      noWrap
      sx={[copyOnClick && { cursor: 'pointer' }]}
    >
      {secret ? 'R$ ***,**' : <MoedaText displayType={'text'} value={valor} />}
    </Typography>
  );
}

export function CelulaNumero({
  numero,
  escala = 0,
  copyOnClick = false,
  secret = false,
}: {
  numero: number;
  escala?: number;
  copyOnClick?: boolean;
  secret?: boolean;
}): JSX.Element {
  return (
    <Typography
      variant="dataMono"
      onClick={
        copyOnClick
          ? (e) => copy((e.target as HTMLElement).innerText)
          : undefined
      }
      noWrap
      sx={[copyOnClick && { cursor: 'pointer' }]}
    >
      {secret ? (
        '*'
      ) : (
        <InteiroText
          displayType={'text'}
          value={numero}
          decimalScale={escala}
        />
      )}
    </Typography>
  );
}

export function CelulaPercentual({
  numero,
  escala = 0,
  copyOnClick = false,
  secret = false,
}: {
  numero: number;
  escala?: number;
  copyOnClick?: boolean;
  secret?: boolean;
}): JSX.Element {
  return (
    <Typography
      variant="dataMono"
      onClick={
        copyOnClick
          ? (e) => copy((e.target as HTMLElement).innerText)
          : undefined
      }
      noWrap
      sx={[copyOnClick && { cursor: 'pointer' }]}
    >
      {secret ? (
        '*'
      ) : (
        <PercentualText
          displayType={'text'}
          value={numero}
          decimalScale={escala}
        />
      )}
    </Typography>
  );
}
